<!-- Copyright (c) 2009-2022. Authors: see NOTICE file.

 Licensed under the Apache License, Version 2.0 (the "License");
 you may not use this file except in compliance with the License.
 You may obtain a copy of the License at

      http://www.apache.org/licenses/LICENSE-2.0

 Unless required by applicable law or agreed to in writing, software
 distributed under the License is distributed on an "AS IS" BASIS,
 WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 See the License for the specific language governing permissions and
 limitations under the License.-->

<template>
<vl-interaction-select
  :ident="`select-target-${index}`"
  :filter="filterFunction"
  :features.sync="selectedFeatures"
  :toggle-condition="never"
  :remove-condition="shiftKeyOnly"
  :multi=true
  ref="interactionSelect"
>
  <vl-style-func :factory="styleFunctionFactory" />
</vl-interaction-select>
</template>

<script>
import WKT from 'ol/format/WKT';
import {isCluster} from '@/utils/style-utils.js';
import {never, shiftKeyOnly} from 'ol/events/condition';
import {isVRBrowser} from '@/utils/vr';

export default {
  name: 'select-interaction',
  props: {
    index: String
  },
  computed: {
    imageModule() {
      return this.$store.getters['currentProject/imageModule'](this.index);
    },
    imageWrapper() {
      return this.$store.getters['currentProject/currentViewer'].images[this.index];
    },
    activeEditTool() {
      return this.imageWrapper.draw.activeEditTool;
    },
    selectedFeatures: {
      get() {
        return this.imageWrapper.selectedFeatures.selectedFeatures;
      },
      set(value) {
        //used when selecting a vertex of a feature
        let notAnnotations = value.filter(x => !Object.keys(x).includes('id') && x.properties === null);
        if (notAnnotations.length === 1) {
          value = notAnnotations;
          this.$store.commit(this.imageModule + 'setSelectedFeatures', value);
          return;
        }

        value.sort(
          function (a, b) {
            if (a.properties.annot.area > b.properties.annot.area) {
              return 1;
            } else {
              return -1;
            }
          }
        );

        let previousTarget = this.imageWrapper.selectedFeatures.selectionTargetedFeatures;
        let previousSelectedFeature = this.imageWrapper.selectedFeatures.selectedFeatures[0];

        this.$store.commit(this.imageModule + 'setSelectionTargetedFeatures', value);

        //see https://github.com/cytomine/Ims-Web-UI/issues/13 for more details of this algorithm
        if (this.imageWrapper.selectedFeatures.selectedFeatures.length === 0) {
          if (value.length >= 1) {
            value = [value[0]];
          }
        } else {
          if (value.length > 1) {
            //if previous selection is in the new target, we will take the first element not yet visited
            if (value.map(x => x.id).includes(previousSelectedFeature.id)) {
              let index = previousTarget.findIndex(x => x.id === previousSelectedFeature.id);
              let visitedFeatures = previousTarget.slice(0, index + 1);
              index = 0;
              for (let i = 0; i < value.length; i++) {
                if (!visitedFeatures.map(x => x.id).includes(value[i].id)) {
                  index = i;
                  break;
                }
              }
              value = [value[index]];
            } else {
              value = [value[0]];
            }
          }
        }

        if (value.length >= 1 && value[0].properties !== null) {
          let annot = value[0].properties.annot;
          if (typeof annot.recordAction === 'function') {
            annot.recordAction();
          }
        }
        this.$store.commit(this.imageModule + 'setSelectedFeatures', value);
        this.$store.commit(this.imageModule + 'setShowSimilarAnnotations', false);
      }
    },
    terms() {
      return this.imageWrapper.style.terms || [];
    },
    styleFunctionFactory() {
      this.imageWrapper.selectedFeatures.selectedFeatures;
      this.imageWrapper.style.layersOpacity;
      this.terms.forEach(term => {
        term.visible;
        term.color;
        term.opacity;
      });
      this.imageWrapper.style.displayNoTerm;
      this.imageWrapper.style.noTermOpacity;
      this.imageWrapper.draw.activeEditTool; // style is different in edit mode (vertices displayed)
      this.imageWrapper.properties.selectedPropertyValues;
      this.imageWrapper.properties.selectedPropertyColor;
      this.imageWrapper.review.reviewMode;
      if (this.imageWrapper.style.wrappedTracks) {
        this.imageWrapper.style.wrappedTracks.forEach(track => {
          track.color;
        });
      }
      return () => {
        return this.$store.getters[this.imageModule + 'genStyleFunction'];
      };
    },
    filterFunction() {
      return feature => !isCluster(feature);
    },
    never() {
      return never;
    },
    shiftKeyOnly() {
      return shiftKeyOnly;
    }
  },
  watch: {
    // P5: preload full geometry the moment an edit tool is armed on a display-only feature.
    //
    // A raster-plane selection carries viewport-simplified geometry; before this, the FIRST
    // drag happened on that outline and was discarded by the endEdit heal ("redo your
    // change") - the one behavioural compromise of the raster plane. Arming an edit tool is
    // the explicit signal of intent to edit, and the fetch usually completes before the drag
    // starts, so the first drag lands on full-fidelity geometry and saves normally. The
    // endEdit heal STAYS as the safety net for a drag that outruns the fetch.
    activeEditTool(tool) {
      if (tool) {
        this.preloadFullGeometry();
      }
    },
    async styleFunctionFactory() {
      // HACK: style function is not called again when redefined => force the update of style for selected features
      this.refreshSelectionRender();
    },
    // Selecting from the annotation list sometimes showed the info card WITHOUT the
    // highlight until the user nudged the map: the store selection updates first, and
    // vuelayers syncs it into the OL interaction asynchronously - when the old refresh ran
    // before that sync it touched an empty collection and nothing repainted until the next
    // view-driven render. Watch the STORE selection and keep nudging (bounded) until the
    // interaction actually contains it, then force the repaint.
    selectedFeatures() {
      this.refreshSelectionRender();
    }
  },
  methods: {
    refreshSelectionRender(attempt = 0) {
      const sel = this.$refs.interactionSelect && this.$refs.interactionSelect.$interaction;
      if (!sel) {
        return;
      }
      sel.getFeatures().forEach(ft => ft.changed());
      sel.changed();
      const want = (this.selectedFeatures || []).map(f => f.id);
      const have = sel.getFeatures().getArray().map(f => f.getId());
      if (attempt < 10 && want.some(id => !have.includes(id))) {
        // vuelayers has not synced the new selection into the interaction yet - try again
        // shortly; 10 x 50 ms bounds the retry well above any observed sync delay
        setTimeout(() => this.refreshSelectionRender(attempt + 1), 50);
      }
    },
    async preloadFullGeometry() {
      const sel = this.$refs.interactionSelect && this.$refs.interactionSelect.$interaction;
      const features = sel && sel.getFeatures ? sel.getFeatures().getArray().slice() : [];
      for (const feature of features) {
        const annot = feature.get('annot');
        if (!annot || !annot._displayOnly) {
          continue;
        }
        try {
          const healed = await annot.clone().fetchFullLocation();
          // the user may have started dragging while we fetched: the endEdit heal owns that
          // case, and swapping geometry mid-drag would fight the interaction
          if (this.imageWrapper.draw.ongoingEdit) {
            continue;
          }
          feature.setGeometry(new WKT().readGeometry(healed.location));
          feature.set('annot', healed);
        } catch (e) {
          // keep the display-only flag: the model still refuses to save, endEdit still heals
          console.log(e);
        }
      }
    },
  },
  async mounted() {
    // VR headset only: widen the pick radius so you don't need pixel-perfect aim at a thin annotation
    // border with a shaky controller pointer. Laptop keeps the default 0px (gated by isVRBrowser()).
    if (!isVRBrowser()) {
      return;
    }
    await this.$refs.interactionSelect.$createPromise;
    let select = this.$refs.interactionSelect.$interaction;
    if (select && select.setHitTolerance) {
      select.setHitTolerance(16);
    }
  }
};
</script>
